import React, { useMemo, useState, useRef, useEffect } from 'react';
import OrphanMode from './OrphanMode';
import '../styles/GraphView.css';

// Layout constants
const NODE_W = 172;
const NODE_H = 46;
const MIN_COL_W = 210;   // narrowest a level column may get before we scroll instead
const ROW_GAP = 22;
const PAD_Y = 26;

const trim = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '');

// Reduce a displayed Key to its prefix form: "TC-8789 (7677)" -> "TC-8789"
const keyPrefix = (k) => {
  if (!k) return '';
  const t = String(k).trim();
  return (t.includes(' ') ? t.split(' ')[0] : t.split('(')[0]).trim();
};

// Classify a test-run result string into ok / fail / unknown
const testOutcome = (val) => {
  if (!val) return null;
  const v = String(val).toLowerCase();
  if (/(fail|block|error)/.test(v)) return 'fail';
  if (/(pass|success|complete|approv)/.test(v)) return 'ok';
  return null; // "No run" / "Not in execution" → not decisive
};

// Short label for the last-run chip shown on test nodes
const runShort = (val) => {
  if (!val) return '';
  const v = String(val).toLowerCase();
  if (/(fail \(stop\)|failstop)/.test(v)) return 'Fail';
  if (/(fail|block|error)/.test(v)) return 'Fail';
  if (/(pass|success|complete|approv)/.test(v)) return 'Pass';
  if (/(not in exec|no run|not run|pending|new)/.test(v)) return 'No run';
  const s = String(val).trim();
  return s.length > 9 ? s.slice(0, 8) + '…' : s;
};

// Is a configured level a Test Run level? (backend flag first, label/code fallback for
// older cached results). A "Test Case" label never counts as a run.
const isRunLevel = (level) => {
  if (!level) return false;
  if (level.is_test_run) return true;
  const lbl = String(level.type_label || level.name || level.work_item_type || '').toLowerCase();
  const code = String(level.work_item_type || '').toUpperCase();
  if (lbl.includes('test case')) return false;
  return lbl.includes('test run') || ['TEST_RUN', 'T_RUN', 'TRUN', 'TR', 'RUN'].includes(code);
};

// Only risks get an icon (a clear ⚠️); every other type shows no icon.
const typeIcon = (type, code) => {
  const s = `${type || ''} ${code || ''}`.toLowerCase();
  return /risk/.test(s) ? '⚠️' : '';
};

// Label for the "＋ Add …" button, mirroring the matrix's Test Run → Test Case
// substitution (a Test Run isn't creatable directly — the backend swaps in a Test
// Case; this only keeps the button wording honest).
const levelAddLabel = (level, typeLabels = {}) => {
  if (!level) return 'item';
  // Prefer the work-item type's full description (e.g. "Product Requirement") over the
  // user-chosen level name, mirroring the matrix's levelTypeLabel().
  const lbl = String(
    level.type_label || typeLabels[level.work_item_type] || level.name || level.work_item_type || ''
  ).trim();
  const low = lbl.toLowerCase();
  const code = String(level.work_item_type || '').toUpperCase();
  const isRun = !low.includes('test case') &&
    (low.includes('test run') || ['TEST_RUN', 'T_RUN', 'TRUN', 'TR', 'RUN'].includes(code));
  return isRun ? 'Test Case' : (lbl || 'item');
};

// Test Case and Test Run share the code T_CASE, so the type LABEL decides.
const levelTypeText = (level, typeLabels = {}) => String(
  (level && (level.type_label || typeLabels[level.work_item_type] || level.name || level.work_item_type)) || ''
).toLowerCase();

const isRunLevelType = (level, typeLabels) => {
  const low = levelTypeText(level, typeLabels);
  const code = String((level && level.work_item_type) || '').toUpperCase();
  if (low.includes('test case')) return false;
  return low.includes('test run') || ['TEST_RUN', 'T_RUN', 'TRUN', 'TR', 'RUN'].includes(code);
};

const isCaseLevelType = (level, typeLabels) => {
  const low = levelTypeText(level, typeLabels);
  const code = String((level && level.work_item_type) || '').toUpperCase();
  if (low.includes('test run')) return false;
  return low.includes('test case') || code === 'T_CASE';
};

// A Test Run comes from EXECUTING its Test Case — it can't be authored, and substituting a
// Test Case here would just hang a second Test Case off a Test Case. So a Test Case → Test
// Run hop offers no add (mirrors the matrix; the backend refuses it too).
const canAddAtLevel = (parentLevel, childLevel, typeLabels) =>
  !(isRunLevelType(childLevel, typeLabels) && isCaseLevelType(parentLevel, typeLabels));

// Small rotating SVG spinner arc, centered at (cx, cy) — shown while relations load
const Spinner = ({ cx, cy, r = 5.5 }) => (
  <circle
    className="g-spin"
    cx={cx}
    cy={cy}
    r={r}
    fill="none"
    strokeWidth="1.8"
    strokeDasharray="17 10"
    strokeLinecap="round"
  />
);

/**
 * Build nodes + edges from the flattened matrix rows for the current page.
 * Each row is one path through the tree (l1..lN). Nodes are deduped by id;
 * edges connect adjacent present levels within a row.
 */
function buildGraph(rows, levels) {
  const numLevels = levels.length;
  const nodes = new Map();            // id -> node
  const orderInLevel = levels.map(() => []); // levelIdx -> [id...] (first-seen order)
  const edgeSet = new Set();
  const edges = [];

  const levelKey = (i) => `l${levels[i].level_num}`;

  // Block-aware branch-end tracking: a leaf whose block has deeper levels configured is a
  // genuine dead-end; a leaf whose branch legitimately ends (e.g. a Risk with no deeper
  // level) is NOT broken, even though a sibling branch runs deeper. Driven by the
  // backend's per-row `missing_at_depth`.
  const shouldContinue = {}; // leaf id -> true (genuine gap) | false (legit branch end)

  rows.forEach((row) => {
    let prev = null; // { id, levelIdx }
    let leafId = null;
    for (let i = 0; i < numLevels; i++) {
      const item = row[levelKey(i)];
      if (!item) { prev = null; continue; } // gap → chain broken here for this row
      const id = String(item.id);
      if (!nodes.has(id)) {
        const cols = item.columns || {};
        nodes.set(id, {
          id,
          levelIdx: i,
          key: cols.Key || item.name || id,
          name: cols.Name || item.name || '',
          url: item.url || '',
          columns: cols,
          type: item.type || '',
          test: testOutcome(cols['Last Test Run Result']),
          testResult: cols['Last Test Run Result'] || '', // raw last-run status, if the filter returned it
        });
        orderInLevel[i].push(id);
      }
      // Guard against a self-edge: a Test Run shares its Test Case's key/id, so the
      // case->run hop can resolve to the same node id — which would create a cycle.
      if (prev && prev.levelIdx === i - 1 && prev.id !== id) {
        const e = `${prev.id}->${id}`;
        if (!edgeSet.has(e)) { edgeSet.add(e); edges.push({ from: prev.id, to: id }); }
      }
      prev = { id, levelIdx: i };
      leafId = id;
    }
    if (leafId != null && 'missing_at_depth' in row) {
      // A row's leaf genuinely continues (is a gap) only when missing_at_depth is set.
      const isGap = row.missing_at_depth != null;
      if (shouldContinue[leafId] === undefined) shouldContinue[leafId] = isGap;
      else shouldContinue[leafId] = shouldContinue[leafId] || isGap;
    }
  });

  // Test result SOURCE, mirroring the matrix: when the panel has a Test Run level, a Test
  // Case's pass/fail comes from its RUN (the authoritative execution) — not the case's own
  // (possibly stale) column. A case with no run reads as "not executed" (no decisive
  // result), never a stale pass/fail. With no run level, the case keeps its own result.
  const runIdx = levels.findIndex(isRunLevel);
  // Test Cases that DID match a run. A run shares its case's display id, so the two merge
  // into one node and the case is left childless one column short of the last level — it is
  // still a complete chain, not a dead-end (see the self-edge guard above).
  const casesWithRun = new Set();
  if (runIdx > 0) {
    const caseKey = levelKey(runIdx - 1);
    const runKey = levelKey(runIdx);
    const rank = (v) => { const o = testOutcome(v); return o === 'fail' ? 3 : o === 'ok' ? 2 : (v ? 1 : 0); };
    const resultByCase = {};
    rows.forEach((row) => {
      const c = row[caseKey];
      if (!c) return;
      const r = row[runKey];
      if (r) casesWithRun.add(String(c.id));
      const val = (r && r.columns && r.columns['Last Test Run Result']) || '';
      const id = String(c.id);
      if (!(id in resultByCase) || rank(val) > rank(resultByCase[id])) resultByCase[id] = val;
    });
    nodes.forEach((n) => {
      if (n.id in resultByCase) {
        n.testResult = resultByCase[n.id];
        n.test = testOutcome(resultByCase[n.id]);
      }
    });
  }

  // adjacency
  const childrenOf = {}, parentsOf = {};
  nodes.forEach((_, id) => { childrenOf[id] = []; parentsOf[id] = []; });
  edges.forEach((e) => { childrenOf[e.from].push(e.to); parentsOf[e.to].push(e.from); });

  // A childless leaf is a genuine dead-end only if its branch was supposed to continue.
  // `shouldContinue[id] === false` means a legitimate branch end (not broken). When the
  // annotation is absent (older cached result) fall back to the old "not last ⇒ miss".
  const isGenuineDeadEnd = (n) => {
    // A Test Case that matched a run is complete even though the run merged into it and
    // left it childless — checked BEFORE the legacy fallback, which would otherwise flag
    // every executed test in a Test Case -> Test Run panel.
    if (casesWithRun.has(n.id)) return false;
    const flag = shouldContinue[n.id];
    if (flag === false) return false;                 // legit branch end
    if (flag === true) return true;                   // known gap
    return n.levelIdx !== numLevels - 1;              // legacy fallback
  };

  // status: fail (test failed) | miss (dead-ends before its branch should end) | ok.
  // `mustContinue` is kept on the node so the pending-edge recompute (see `derived`) can
  // reuse this block-aware verdict instead of re-deriving a cruder one.
  nodes.forEach((n) => {
    n.mustContinue = isGenuineDeadEnd(n);
    if (n.test === 'fail') n.status = 'fail';
    else if (childrenOf[n.id].length === 0 && n.mustContinue) n.status = 'miss';
    else n.status = 'ok';
  });

  // Does this node's chain fail to reach a passing endpoint / legitimate branch end?
  const deadCache = {};
  const inProgress = new Set(); // cycle guard: a node re-entered mid-recursion (should never happen in a DAG)
  const deadEnds = (id) => {
    if (id in deadCache) return deadCache[id];
    if (inProgress.has(id)) return false;   // break the cycle without blowing the stack
    inProgress.add(id);
    const n = nodes.get(id);
    let res;
    if (!n) {
      res = false;
    } else if (childrenOf[id].length === 0) {
      // Leaf: broken if it's a genuine dead-end, or a failed test at the last level.
      res = n.test === 'fail' ? true : isGenuineDeadEnd(n);
    } else {
      res = childrenOf[id].every((c) => deadEnds(c));
    }
    inProgress.delete(id);
    deadCache[id] = res;
    return res;
  };
  nodes.forEach((n) => { n.broken = deadEnds(n.id); });

  const maxRows = Math.max(1, ...orderInLevel.map((a) => a.length));

  return {
    nodes, edges, childrenOf, parentsOf, deadEnds,
    orderInLevel, maxRows, numLevels,
  };
}

// Position nodes into columns that stretch to fill `availWidth`. Columns never
// go below MIN_COL_W — past that the stage grows wider than the container and
// the scroll area takes over. Nodes are centered within their (wider) column.
function layoutGraph(topo, availWidth) {
  const { nodes, orderInLevel, maxRows, numLevels } = topo;
  const colW = Math.max(MIN_COL_W, Math.floor((availWidth || 0) / numLevels));
  const width = numLevels * colW;
  const height = PAD_Y * 2 + maxRows * NODE_H + (maxRows - 1) * ROW_GAP;

  orderInLevel.forEach((ids, li) => {
    const totalH = ids.length * NODE_H + (ids.length - 1) * ROW_GAP;
    const startY = (height - totalH) / 2;
    const x = li * colW + (colW - NODE_W) / 2; // center the node box in its column
    ids.forEach((id, ri) => {
      const n = nodes.get(id);
      n.x = x;
      n.y = startY + ri * (NODE_H + ROW_GAP);
      n.cx = n.x + NODE_W / 2;
      n.cy = n.y + NODE_H / 2;
    });
  });

  return { width, height, colW };
}

// Cubic bezier from a parent's right edge to a child's left edge
const linkPath = (a, b) => {
  const x1 = a.x + NODE_W, y1 = a.cy, x2 = b.x, y2 = b.cy;
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
};

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2.2;
const ZOOM_STEP = 0.2;
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

export default function GraphView({ rows, levels, canAdd = true, searchTerm = '', onCreateRelation, onDeleteRelation, canDeleteTraceAt = () => false, onReconcile, onFetchOrphans, onFetchParents, onFetchRelations, onAddChild, canAddChildType = () => true, typeLabels = {} }) {
  const [focusId, setFocusId] = useState(null);
  const [brokenOnly, setBrokenOnly] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [orphanLevel, setOrphanLevel] = useState(null); // active orphan-mode level, or null

  // ---- external relations (items outside the panel filter scope: risks, defects, CRs) ----
  const [extNodes, setExtNodes] = useState({});          // id -> floating node w/ x,y,cx,cy,sourceId,depth
  const [extEdges, setExtEdges] = useState([]);          // { from, to, existing, dir }
  const [expandedIds, setExpandedIds] = useState(() => new Set()); // node ids with relations fetched
  const [hiddenSources, setHiddenSources] = useState(() => new Set()); // fetched-but-hidden sources
  const [relLoadingId, setRelLoadingId] = useState(null);
  const canRelations = typeof onFetchRelations === 'function';

  // Levels that have an orphan filter configured → can enter Orphan Mode
  const orphanableLevels = useMemo(
    () => (levels || []).filter((l) => l.has_orphans),
    [levels],
  );
  const canOrphan = canAdd && typeof onFetchOrphans === 'function' && typeof onCreateRelation === 'function' && orphanableLevels.length > 0;

  // Can open the same add-item form the matrix uses (adds a child at the next level)
  const canAddChild = canAdd && typeof onAddChild === 'function';

  // ---- link (drag-to-trace) mode ----
  const [linkMode, setLinkMode] = useState(false);
  const [drag, setDrag] = useState(null);           // { sourceId, x, y, hoverId }
  const [confirm, setConfirm] = useState(null);      // { sourceId, targetId }
  const [pendingEdges, setPendingEdges] = useState([]); // optimistic edges [{from,to}]
  const [linking, setLinking] = useState(false);
  const [toast, setToast] = useState(null);          // { msg, kind, retry? }
  const dragRef = useRef(null);
  const svgRef = useRef(null);
  const toastTimer = useRef(null);

  const canLink = canAdd && typeof onCreateRelation === 'function';

  // ---- select a link (click it) → delete it (QW_Delete_Relation) ----
  const [selEdge, setSelEdge] = useState(null);         // { from, to } — the marked link
  const [delConfirm, setDelConfirm] = useState(null);   // { from, to } awaiting confirmation
  const [removedEdges, setRemovedEdges] = useState([]); // optimistic: deleted, until reconcile
  const [deleting, setDeleting] = useState(false);
  const sameEdge = (a, b) => !!a && !!b && a.from === b.from && a.to === b.to;

  // Measure the scroll container so columns can stretch to fill the screen.
  // Re-runs when leaving Orphan Mode: that unmounts the scroll stage and mounts a
  // fresh one, which must be re-observed/re-measured or the graph stays collapsed left.
  const scrollRef = useRef(null);
  const [availWidth, setAvailWidth] = useState(0);
  useEffect(() => {
    if (orphanLevel != null) return undefined;   // stage not mounted while in Orphan Mode
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setAvailWidth(el.clientWidth);
    measure();
    // clientWidth can read 0 on the first commit after remount; re-measure next frame
    const raf = (typeof requestAnimationFrame !== 'undefined') ? requestAnimationFrame(measure) : null;
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [orphanLevel]);

  const topo = useMemo(() => buildGraph(rows || [], levels || []), [rows, levels]);
  const g = useMemo(() => ({ ...topo, ...layoutGraph(topo, availWidth) }), [topo, availWidth]);
  const { nodes } = g;
  // Links deleted this session vanish at once; the reconcile refetch then confirms it.
  const edges = useMemo(
    () => (removedEdges.length ? g.edges.filter((e) => !removedEdges.some((r) => sameEdge(r, e))) : g.edges),
    [g.edges, removedEdges],
  );

  // Node ids currently hidden because an ancestor source is hidden (data retained). (#5)
  const hiddenNodeIds = useMemo(() => {
    const hidden = new Set();
    const walk = (id) => extEdges.forEach((e) => {
      if (e.from === id && !e.existing && extNodes[e.to] && !hidden.has(e.to)) {
        hidden.add(e.to); walk(e.to);
      }
    });
    hiddenSources.forEach((s) => walk(s));
    return hidden;
  }, [hiddenSources, extEdges, extNodes]);

  // original work-item CODE (e.g. "REQ") -> custom/display prefix (e.g. "PR"), learned from the
  // in-scope nodes already on the graph (their key IS the custom code, their level carries the
  // original code). Lets related items show the SAME custom code the matrix uses.
  const origToCustom = useMemo(() => {
    const m = {};
    nodes.forEach((n) => {
      const lvl = levels[n.levelIdx];
      const orig = lvl && lvl.work_item_type ? String(lvl.work_item_type).toUpperCase() : '';
      const cp = keyPrefix(n.key).split('-')[0];
      if (orig && cp && /[A-Za-z]/.test(cp)) m[orig] = cp;
    });
    return m;
  }, [nodes, levels]);

  // Stage grows to fit any floating external nodes (they can sit right of the last column).
  const extBounds = useMemo(() => {
    const vals = Object.values(extNodes).filter((n) => !hiddenNodeIds.has(n.id));
    if (vals.length === 0) return { w: g.width, h: g.height };
    let maxX = g.width, maxY = g.height;
    vals.forEach((n) => {
      maxX = Math.max(maxX, n.x + NODE_W);
      maxY = Math.max(maxY, n.y + (n.h || NODE_H));
    });
    return { w: maxX + 40, h: maxY + 40 };
  }, [extNodes, hiddenNodeIds, g.width, g.height]);

  // ---- pan (drag empty canvas to scroll) + fit / reset view ----
  const panRef = useRef(null);            // { startX, startY, sl, st, moved }
  const [panning, setPanning] = useState(false);
  const didPanRef = useRef(false);        // true if the last pointer gesture actually panned
  const lvlbarRef = useRef(null);

  // Position the viewport after a zoom change has committed + reflowed.
  const scrollAfter = (mode) => {
    const run = () => {
      const el = scrollRef.current;
      if (!el) return;
      if (mode === 'center') {
        el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
        el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
      } else if (mode === 'topleft') {
        el.scrollLeft = 0; el.scrollTop = 0;
      }
    };
    if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(() => requestAnimationFrame(run));
    else run();
  };

  // Reset to 100% and center the graph in the viewport.
  const resetView = () => { setZoom(1); scrollAfter('center'); };

  // Zoom out just enough to show the whole graph on screen (never zooms past 100%,
  // since columns already stretch to fill the width at 1×).
  const fitToScreen = () => {
    const el = scrollRef.current;
    if (!el || !g.width || !g.height) return;
    const barH = (lvlbarRef.current && lvlbarRef.current.offsetHeight) || 46;
    const z = Math.min(el.clientWidth / g.width, (el.clientHeight - barH) / g.height);
    setZoom(clampZoom(z));
    scrollAfter('topleft');
  };

  const onStagePointerDown = (e) => {
    // Pan only from empty canvas — a node handles its own click/drag.
    if (e.target.closest && e.target.closest('.g-node')) return;
    if (linkMode && drag) return;
    const el = scrollRef.current;
    if (!el) return;
    panRef.current = { startX: e.clientX, startY: e.clientY, sl: el.scrollLeft, st: el.scrollTop, moved: false };
    didPanRef.current = false;
    setPanning(true);
  };

  useEffect(() => {
    if (!panning) return undefined;
    const move = (e) => {
      const p = panRef.current, el = scrollRef.current;
      if (!p || !el) return;
      const dx = e.clientX - p.startX, dy = e.clientY - p.startY;
      if (!p.moved && Math.abs(dx) + Math.abs(dy) > 4) { p.moved = true; didPanRef.current = true; }
      el.scrollLeft = p.sl - dx;
      el.scrollTop = p.st - dy;
    };
    const up = () => { setPanning(false); panRef.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [panning]);

  // A reconcile refetch (new rows) arrived → optimistic edges are now truth-checked
  useEffect(() => { setPendingEdges([]); setRemovedEdges([]); setSelEdge(null); }, [rows]);

  // New page / rebuild → drop any external-relation overlay (its ids may no longer exist)
  useEffect(() => {
    setExtNodes({}); setExtEdges([]); setExpandedIds(new Set());
    setHiddenSources(new Set()); setRelLoadingId(null);
  }, [rows]);

  // Adjacency + broken status, recomputed to INCLUDE optimistic pending edges, so an
  // item you just traced to stops looking "missing" the instant you link it (before
  // the reconcile refetch lands).
  const derived = useMemo(() => {
    const childrenOf = {}, parentsOf = {};
    nodes.forEach((_, id) => { childrenOf[id] = []; parentsOf[id] = []; });
    [...edges, ...pendingEdges].forEach((e) => {
      if (childrenOf[e.from] && parentsOf[e.to]) {
        childrenOf[e.from].push(e.to);
        parentsOf[e.to].push(e.from);
      }
    });
    // Reuse buildGraph's block-aware `mustContinue` (driven by the backend's
    // `missing_at_depth`, and false for a Test Case whose run merged into it). Re-deriving
    // it from levelIdx here would flag every legitimate branch end and every executed test.
    nodes.forEach((n) => {
      if (n.test === 'fail') n.status = 'fail';
      else if (childrenOf[n.id].length === 0 && n.mustContinue) n.status = 'miss';
      else n.status = 'ok';
    });
    const cache = {};
    const inProgress = new Set(); // cycle guard — a pending edge can close a loop
    const deadEnds = (id) => {
      if (id in cache) return cache[id];
      if (inProgress.has(id)) return false;
      inProgress.add(id);
      const n = nodes.get(id);
      let res;
      if (!n) res = false;
      else if (childrenOf[id].length === 0) res = n.test === 'fail' ? true : !!n.mustContinue;
      else res = childrenOf[id].every(deadEnds);
      inProgress.delete(id);
      cache[id] = res;
      return res;
    };
    nodes.forEach((n) => {
      n.broken = deadEnds(n.id);                          // whole-chain incomplete — used by the filter only
      n.isBreak = n.status === 'miss' || n.status === 'fail'; // THIS item is the break point — used for the glow
    });
    return { childrenOf, parentsOf, deadEnds };
  }, [nodes, edges, pendingEdges]);
  const { childrenOf, parentsOf } = derived;

  // Set of existing links (both real + optimistic) for "already linked" checks
  const linkSet = useMemo(() => {
    const s = new Set();
    edges.forEach((e) => s.add(`${e.from}|${e.to}`));
    return s;
  }, [edges]);
  const isLinked = (a, b) =>
    linkSet.has(`${a}|${b}`) || linkSet.has(`${b}|${a}`) ||
    pendingEdges.some((e) => (e.from === a && e.to === b) || (e.from === b && e.to === a));

  // Set of node ids in the focused chain (all ancestors + all descendants)
  const chainSet = useMemo(() => {
    if (!focusId) return null;
    const seen = new Set([focusId]);
    const up = (x) => parentsOf[x]?.forEach((p) => { if (!seen.has(p)) { seen.add(p); up(p); } });
    const down = (x) => childrenOf[x]?.forEach((c) => { if (!seen.has(c)) { seen.add(c); down(c); } });
    up(focusId); down(focusId);
    return seen;
  }, [focusId, childrenOf, parentsOf]);

  // Set of node ids that belong to any broken chain (for "Only broken" view)
  const brokenSet = useMemo(() => {
    if (!brokenOnly) return null;
    const lit = new Set();
    nodes.forEach((n) => {
      if (n.broken) {
        const s = new Set([n.id]);
        const up = (x) => parentsOf[x]?.forEach((p) => { if (!s.has(p)) { s.add(p); up(p); } });
        const down = (x) => childrenOf[x]?.forEach((c) => { if (!s.has(c)) { s.add(c); down(c); } });
        up(n.id); down(n.id);
        s.forEach((id) => lit.add(id));
      }
    });
    return lit;
  }, [brokenOnly, nodes, childrenOf, parentsOf]);

  // Client-side highlight: which on-page nodes match the active search term (any
  // level, by key / name / id). The whole-panel L1 filter already narrows the
  // chains server-side; this makes the matched item pop within them.
  // Must stay above the early returns below — a hook after a conditional return changes
  // the hook count between renders (empty page -> populated page) and React throws.
  const matchSet = useMemo(() => {
    const q = String(searchTerm || '').trim().toLowerCase();
    if (!q) return null;
    const s = new Set();
    nodes.forEach((n) => {
      const hay = `${keyPrefix(n.key)} ${n.name || ''} ${n.id}`.toLowerCase();
      if (hay.includes(q)) s.add(n.id);
    });
    return s;
  }, [searchTerm, nodes]);

  // ---- toast helper ----
  const showToast = (msg, kind = 'ok', retry) => {
    setToast({ msg, kind, retry });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), retry ? 14000 : 3200);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // ---- coordinate + hit-test helpers (viewBox space, zoom/scroll-safe) ----
  const toSvgPoint = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg || !svg.createSVGPoint) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };
  const hitNode = (x, y) => {
    for (const n of nodes.values()) {
      if (x >= n.x && x <= n.x + NODE_W && y >= n.y && y <= n.y + NODE_H) return n.id;
    }
    return null;
  };

  // Per-node state during a drag: source | valid | linked | invalid | null
  const dragState = (nodeId) => {
    if (!drag) return null;
    if (nodeId === drag.sourceId) return 'source';
    const s = nodes.get(drag.sourceId), t = nodes.get(nodeId);
    if (!s || !t) return 'invalid';
    if (Math.abs(s.levelIdx - t.levelIdx) === 1) {
      return isLinked(drag.sourceId, nodeId) ? 'linked' : 'valid';
    }
    return 'invalid';
  };

  // ---- drag lifecycle ----
  const setDragState = (d) => { dragRef.current = d; setDrag(d); };

  const startLinkDrag = (nodeId, e) => {
    if (!linkMode || !canLink || confirm) return;
    e.stopPropagation();
    e.preventDefault();
    const p = toSvgPoint(e.clientX, e.clientY);
    setFocusId(null);
    setDragState({ sourceId: nodeId, x: p.x, y: p.y, hoverId: null });
  };

  const finishDrag = (targetId) => {
    const d = dragRef.current;
    setDragState(null);
    if (!d || !targetId || targetId === d.sourceId) return; // snap back silently
    const s = nodes.get(d.sourceId), t = nodes.get(targetId);
    if (!s || !t) return;
    if (Math.abs(s.levelIdx - t.levelIdx) !== 1) {
      showToast('Traces can only be created between adjacent levels', 'warn');
      return;
    }
    if (isLinked(d.sourceId, targetId)) {
      showToast('These items are already linked', 'info');
      return;
    }
    setConfirm({ sourceId: d.sourceId, targetId });
  };

  // Bind window listeners for the duration of a drag session
  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      const p = toSvgPoint(e.clientX, e.clientY);
      const hoverId = hitNode(p.x, p.y);
      setDragState({ ...dragRef.current, x: p.x, y: p.y, hoverId });
    };
    const up = (e) => {
      const p = toSvgPoint(e.clientX, e.clientY);
      finishDrag(hitNode(p.x, p.y));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drag && drag.sourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving link mode cancels any in-flight drag/confirm
  useEffect(() => {
    if (!linkMode) { setDragState(null); setConfirm(null); }
  }, [linkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- create the relation (deeper -> shallower), optimistic + reconcile ----
  const buildAttempt = (sourceId, targetId) => {
    const a = nodes.get(sourceId), b = nodes.get(targetId);
    const deeper = a.levelIdx > b.levelIdx ? a : b;
    const shallower = a.levelIdx > b.levelIdx ? b : a;
    return {
      sourceKey: keyPrefix(deeper.key),
      targetKey: keyPrefix(shallower.key),
      sourceLevel: levels[deeper.levelIdx].level_num,
      targetLevel: levels[shallower.levelIdx].level_num,
      edge: { from: shallower.id, to: deeper.id }, // layout draws shallower -> deeper
    };
  };

  const runAttempt = async (attempt) => {
    setPendingEdges((p) => (p.includes(attempt.edge) ? p : [...p, attempt.edge]));
    setLinking(true);
    const res = await onCreateRelation(attempt);
    setLinking(false);
    if (res && res.ok) {
      showToast(`Linked ${attempt.sourceKey} → ${attempt.targetKey}`, 'ok');
      if (onReconcile) onReconcile(); // background refetch; pendingEdges cleared when rows change
    } else {
      setPendingEdges((p) => p.filter((e) => e !== attempt.edge));
      showToast(
        `Couldn't link ${attempt.sourceKey} → ${attempt.targetKey}: ${(res && res.error) || 'unknown error'}`,
        'warn',
        () => runAttempt(attempt),
      );
    }
  };

  const doConfirm = () => {
    if (!confirm) return;
    const attempt = buildAttempt(confirm.sourceId, confirm.targetId);
    setConfirm(null);
    runAttempt(attempt);
  };

  // ---- delete the selected link (optimistic + reconcile) ----
  const canDeleteEdge = (e) => {
    if (!e || typeof onDeleteRelation !== 'function') return false;
    const deeper = nodes.get(e.to);                       // layout draws shallower -> deeper
    return !!deeper && !!canDeleteTraceAt(deeper.levelIdx);
  };

  const runDelete = async (edge) => {
    const attempt = buildAttempt(edge.from, edge.to);
    setRemovedEdges((r) => (r.some((x) => sameEdge(x, edge)) ? r : [...r, edge]));
    setSelEdge(null);
    setDeleting(true);
    const res = await onDeleteRelation(attempt);
    setDeleting(false);
    if (res && res.ok) {
      showToast(`Deleted trace ${attempt.sourceKey} → ${attempt.targetKey}`, 'ok');
      if (onReconcile) onReconcile(); // removedEdges cleared when rows change
    } else {
      setRemovedEdges((r) => r.filter((x) => !sameEdge(x, edge)));
      showToast(
        `Couldn't delete ${attempt.sourceKey} → ${attempt.targetKey}: ${(res && res.error) || 'unknown error'}`,
        'warn',
        () => runDelete(edge),
      );
    }
  };

  const doDeleteConfirm = () => {
    if (!delConfirm) return;
    const edge = delConfirm;
    setDelConfirm(null);
    runDelete(edge);
  };

  // Keyboard: Delete / Backspace on a marked link asks to delete it; Escape unmarks it.
  useEffect(() => {
    if (!selEdge) return undefined;
    const onKey = (ev) => {
      const tag = ev.target && ev.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (ev.key === 'Escape') setSelEdge(null);
      else if ((ev.key === 'Delete' || ev.key === 'Backspace') && canDeleteEdge(selEdge)) {
        ev.preventDefault();
        setDelConfirm(selEdge);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selEdge]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- external relations: fetch one hop, place floating nodes near the source ----
  const nodePos = (id) => nodes.get(id) || extNodes[id] || null;

  // internal id (the form the relations API often wants) parsed from an in-scope node's url
  const linkIdOf = (n) => {
    const m = /ItemId=(\d+)/.exec(n && n.url ? n.url : '');
    return m ? m[1] : '';
  };

  const EXT_GAP_X = 96;
  const EXT_H = 54;                       // external nodes are a touch taller (badge + key + name)
  const EXT_ROW = EXT_H + 18;

  const rectsOverlap = (a, b) =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

  // Place external children to the right of the source, nudging each to a free slot so it
  // doesn't sit on top of an existing node (in-scope or already-floating). (#3)
  const positionExternals = (src, list) => {
    // occupied rectangles already on the canvas
    const occ = [];
    nodes.forEach((n) => occ.push({ x: n.x, y: n.y, w: NODE_W, h: NODE_H }));
    Object.values(extNodes).forEach((n) => occ.push({ x: n.x, y: n.y, w: NODE_W, h: EXT_H }));

    const out = {};
    const startY = Math.max(PAD_Y, src.cy - ((list.length - 1) * EXT_ROW) / 2 - EXT_H / 2);
    list.forEach((nd, i) => {
      let x = src.x + NODE_W + EXT_GAP_X;
      let y = startY + i * EXT_ROW;
      // scan for a non-overlapping slot: step down, then shift a column right and retry
      for (let col = 0; col < 6; col++) {
        let placed = false;
        for (let step = 0; step < 40; step++) {
          const cand = { x, y, w: NODE_W, h: EXT_H };
          if (!occ.some((r) => rectsOverlap(cand, r))) { placed = true; break; }
          y += EXT_ROW / 2;
        }
        if (placed) break;
        x += NODE_W + EXT_GAP_X;
        y = startY + i * EXT_ROW;
      }
      const rect = { x, y, w: NODE_W, h: EXT_H };
      occ.push(rect);
      out[nd.id] = {
        ...nd, x, y, h: EXT_H,
        cx: x + NODE_W / 2, cy: y + EXT_H / 2,
        sourceId: src.id, depth: (src.depth || 0) + 1,
      };
    });
    return out;
  };

  // Discard an item's external subtree entirely (Close). (#5)
  const closeRelations = (srcId) => {
    const remove = new Set();
    const walk = (id) => {
      extEdges.forEach((e) => {
        if (e.from === id && !e.existing && extNodes[e.to] && !remove.has(e.to)) {
          remove.add(e.to); walk(e.to);
        }
      });
    };
    walk(srcId);
    setExtNodes((prev) => {
      const next = { ...prev };
      remove.forEach((id) => { delete next[id]; });
      return next;
    });
    setExtEdges((prev) => prev.filter((e) => e.from !== srcId && !remove.has(e.from) && !remove.has(e.to)));
    setExpandedIds((prev) => {
      const s = new Set(prev); s.delete(srcId); remove.forEach((id) => s.delete(id)); return s;
    });
    setHiddenSources((prev) => { const s = new Set(prev); s.delete(srcId); return s; });
  };

  // Hide keeps the fetched data (no refetch on Show). (#5)
  const hideRelations = (srcId) => setHiddenSources((p) => new Set(p).add(srcId));
  const showHidden = (srcId) => setHiddenSources((p) => { const s = new Set(p); s.delete(srcId); return s; });

  const expandRelations = async (srcId) => {
    if (!canRelations || relLoadingId) return;
    const src = nodePos(srcId);
    if (!src) return;
    setRelLoadingId(srcId);
    // Exclude everything already on screen → only genuinely out-of-scope items come back new.
    const ids = [], keys = [];
    nodes.forEach((n) => { ids.push(n.id); keys.push(keyPrefix(n.key)); });
    Object.values(extNodes).forEach((n) => { ids.push(n.id); keys.push(n.key); });
    const isExt = !!extNodes[srcId];
    const res = await onFetchRelations({
      itemId: isExt ? (src.raw_id || src.id) : src.id,
      linkId: isExt ? '' : linkIdOf(src),
      exclude: ids.join(','),
      excludeKeys: keys.join(','),
    });
    setRelLoadingId(null);
    if (!res || !res.ok) {
      showToast(`Couldn't load related items: ${(res && res.error) || 'error'}`, 'warn');
      return;
    }
    const links = res.links || [];
    const positioned = positionExternals(src, res.nodes || []);
    setExtNodes((prev) => ({ ...prev, ...positioned }));
    setExtEdges((prev) => {
      const seen = new Set(prev.map((e) => `${e.from}|${e.to}`));
      const add = links
        .map((l) => ({ from: srcId, to: l.to, existing: !!l.existing, dir: l.dir }))
        .filter((e) => positioned[e.to] || nodePos(e.to))          // target must be placeable
        .filter((e) => { const k = `${e.from}|${e.to}`; if (seen.has(k)) return false; seen.add(k); return true; });
      return [...prev, ...add];
    });
    setExpandedIds((p) => new Set(p).add(srcId));
    setHiddenSources((p) => { const s = new Set(p); s.delete(srcId); return s; });
    const nNew = Object.keys(positioned).length;
    if (nNew === 0) showToast('No related items outside this panel', 'info');
    else showToast(`+${nNew} related item${nNew > 1 ? 's' : ''} outside scope`, 'ok');
  };

  // The on-node pulse button + amber-node click: open, or toggle hide/show once fetched.
  const toggleRelations = (srcId) => {
    if (!expandedIds.has(srcId)) expandRelations(srcId);
    else if (hiddenSources.has(srcId)) showHidden(srcId);
    else hideRelations(srcId);
  };

  // Curve between two node centers (used for floating external edges)
  const extPath = (a, b) => {
    const mx = (a.cx + b.cx) / 2;
    return `M${a.cx},${a.cy} C${mx},${a.cy} ${mx},${b.cy} ${b.cx},${b.cy}`;
  };

  // Orphan Mode takes over the whole canvas (its own header has the exit).
  if (orphanLevel != null) {
    return (
      <div className="graph-view">
        <OrphanMode
          orphanLevel={orphanLevel}
          levels={levels}
          onFetchOrphans={onFetchOrphans}
          onFetchParents={onFetchParents}
          onCreateRelation={onCreateRelation}
          onReconcile={onReconcile}
          onExit={() => setOrphanLevel(null)}
        />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return <div className="graph-empty">No items on this page.</div>;
  }

  const focused = focusId ? nodes.get(focusId) : null;

  // Confirm dialog needs the resolved direction
  const confirmInfo = confirm ? buildAttempt(confirm.sourceId, confirm.targetId) : null;

  const nodeCls = (n) => {
    const cls = ['g-node', `g-${n.status}`];
    if (matchSet && matchSet.has(n.id)) cls.push('g-match'); // search hit — ring it so it pops
    if (n.isBreak) cls.push('g-broken'); // glow only the actual break point, not its ancestors
    // persistent marker on a node whose relations are open — so you can find your way back
    // to it even after focus is lost (#11)
    if (expandedIds.has(n.id)) cls.push('g-relsrc');
    if (linkMode && canLink) cls.push('g-linkable');
    const ds = dragState(n.id);
    if (ds) {
      cls.push(`g-drag-${ds}`);
    } else if (focusId) {
      if (n.id === focusId) cls.push('g-focus');
      else if (chainSet.has(n.id)) cls.push('g-chain');
      else cls.push('g-dim');
    } else if (brokenSet && !brokenSet.has(n.id)) {
      cls.push('g-dim');
    }
    return cls.join(' ');
  };

  const linkCls = (e, pending) => {
    const cls = ['g-link'];
    if (pending) { cls.push('g-link-pending'); return cls.join(' '); }
    if (sameEdge(e, selEdge)) { cls.push('g-link-sel'); return cls.join(' '); }
    if (drag) { cls.push('g-link-dim'); return cls.join(' '); }
    if (focusId) {
      const inChain = chainSet.has(e.from) && chainSet.has(e.to);
      cls.push(inChain ? 'g-link-chain' : 'g-link-dim');
    } else if (brokenSet && !(brokenSet.has(e.from) && brokenSet.has(e.to))) {
      cls.push('g-link-dim');
    }
    return cls.join(' ');
  };

  const dragSource = drag ? nodes.get(drag.sourceId) : null;

  const selInfo = selEdge && nodes.get(selEdge.from) && nodes.get(selEdge.to)
    ? buildAttempt(selEdge.from, selEdge.to) : null;
  const delInfo = delConfirm && nodes.get(delConfirm.from) && nodes.get(delConfirm.to)
    ? buildAttempt(delConfirm.from, delConfirm.to) : null;

  return (
    <div className="graph-view">
      <div className="graph-controls">
        <span className="graph-legend">
          {linkMode ? (
            <span className="graph-hint graph-hint-link">
              Drag an item onto an <b>adjacent</b> level to trace it (recorded deeper → shallower).
            </span>
          ) : (
            <>
              <span className="lg lg-ok" /> Covered
              <span className="lg lg-fail" /> Failed test
              <span className="lg lg-miss" /> Chain breaks
              <span className="graph-hint">· click a node to focus its chain, a link to select it</span>
            </>
          )}
        </span>

        {canLink && (
          <button
            type="button"
            className={`graph-btn${linkMode ? ' on' : ''}`}
            onClick={() => { setLinkMode((v) => !v); setFocusId(null); setBrokenOnly(false); setSelEdge(null); }}
            title="Toggle drag-to-trace"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M6.5 9.5 9.5 6.5M5 8 3.5 9.5a2.1 2.1 0 0 0 3 3L8 11M11 8l1.5-1.5a2.1 2.1 0 0 0-3-3L8 5" strokeLinecap="round" />
            </svg>
            Link mode
          </button>
        )}

        {!linkMode && (
          <button
            type="button"
            className={`graph-btn${brokenOnly ? ' on' : ''}`}
            onClick={() => { setBrokenOnly((v) => !v); setFocusId(null); }}
          >
            <span className="sw" /> Only broken chains
          </button>
        )}
        {focusId && !linkMode && (
          <button type="button" className="graph-btn" onClick={() => setFocusId(null)}>
            Reset focus
          </button>
        )}

        {canOrphan && !linkMode && (
          <label className="graph-orphan-pick" title="Connect untraced (orphan) items at a level">
            <span className="gop-lbl">Orphans</span>
            <select
              value=""
              onChange={(e) => { const v = e.target.value; if (v) setOrphanLevel(Number(v)); }}
            >
              <option value="">Pick a level…</option>
              {orphanableLevels.map((l) => (
                <option key={l.level_num} value={l.level_num}>L{l.level_num} · {l.name}</option>
              ))}
            </select>
          </label>
        )}

        <button
          type="button"
          className="graph-btn"
          onClick={fitToScreen}
          title="Fit the whole graph on screen"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Fit
        </button>

        <div className="graph-zoom" role="group" aria-label="Zoom">
          <button
            type="button"
            className="gz-btn"
            onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
            disabled={zoom <= ZOOM_MIN}
            title="Zoom out"
            aria-label="Zoom out"
          >−</button>
          <button
            type="button"
            className="gz-level"
            onClick={resetView}
            title="Reset to 100% and center"
          >{Math.round(zoom * 100)}%</button>
          <button
            type="button"
            className="gz-btn"
            onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
            disabled={zoom >= ZOOM_MAX}
            title="Zoom in"
            aria-label="Zoom in"
          >+</button>
        </div>
      </div>

      <div
        className={`graph-scroll${panning ? ' is-panning' : ''}`}
        ref={scrollRef}
        onPointerDown={onStagePointerDown}
      >
        <div className="graph-stage" style={{ width: extBounds.w * zoom }}>
          <div ref={lvlbarRef} className="graph-lvlbar" style={{ gridTemplateColumns: `repeat(${g.numLevels}, ${g.colW * zoom}px)`, width: g.width * zoom }}>
            {levels.map((l, i) => (
              <div className={`glh${i === levels.length - 1 ? ' final' : ''}`} key={i}>
                <div className="glh-code">L{l.level_num}</div>
                <div className="glh-name">{l.name}</div>
              </div>
            ))}
          </div>

          <svg
            ref={svgRef}
            className={`graph-svg${drag ? ' is-dragging' : ''}`}
            width={extBounds.w * zoom}
            height={extBounds.h * zoom}
            viewBox={`0 0 ${extBounds.w} ${extBounds.h}`}
            onClick={() => {
              if (didPanRef.current) { didPanRef.current = false; return; } // ignore the click that ends a pan
              if (!linkMode) { setFocusId(null); setSelEdge(null); }
            }}
          >
            {edges.map((e, i) => {
              const a = nodes.get(e.from), b = nodes.get(e.to);
              if (!a || !b) return null;
              return <path key={`e${i}`} className={linkCls(e, false)} d={linkPath(a, b)} />;
            })}
            {/* wide invisible hit paths — click a link to select (mark) it */}
            {!linkMode && !drag && edges.map((e, i) => {
              const a = nodes.get(e.from), b = nodes.get(e.to);
              if (!a || !b) return null;
              return (
                <path
                  key={`h${i}`}
                  className="g-link-hit"
                  d={linkPath(a, b)}
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setSelEdge((cur) => (sameEdge(cur, e) ? null : { from: e.from, to: e.to }));
                  }}
                >
                  <title>{`${keyPrefix(b.key)} → ${keyPrefix(a.key)} — click to select`}</title>
                </path>
              );
            })}
            {pendingEdges.map((e, i) => {
              const a = nodes.get(e.from), b = nodes.get(e.to);
              if (!a || !b) return null;
              return <path key={`p${i}`} className={linkCls(e, true)} d={linkPath(a, b)} />;
            })}

            {/* live drag line from source to cursor */}
            {dragSource && (
              <g className="g-drag-linewrap">
                <path className="g-drag-line" d={`M${dragSource.cx},${dragSource.cy} L${drag.x},${drag.y}`} />
                <circle className="g-drag-dot" cx={drag.x} cy={drag.y} r="4" />
              </g>
            )}

            {[...nodes.values()].map((n) => {
              const ds = dragState(n.id);
              const isFocus = n.id === focusId;
              const relOpen = expandedIds.has(n.id);
              const relHidden = hiddenSources.has(n.id);
              // pulse the on-node "related" button when focused & nothing shown yet (invites a click)
              const relPulse = isFocus && !linkMode && (!relOpen || relHidden);
              return (
                <g
                  key={n.id}
                  className={nodeCls(n)}
                  transform={`translate(${n.x},${n.y})`}
                  onPointerDown={(e) => startLinkDrag(n.id, e)}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    if (!linkMode) setFocusId(n.id === focusId ? null : n.id);
                  }}
                >
                  <title>{n.name ? `${keyPrefix(n.key)} — ${n.name}` : keyPrefix(n.key)}</title>
                  <rect className="g-box" x="0" y="0" width={NODE_W} height={NODE_H} rx="9" />
                  <circle className="g-dot" cx="14" cy="15" r="4" />
                  <text className="g-key" x="26" y="19">{trim(keyPrefix(n.key), 16)}</text>
                  <text className="g-lbl" x="14" y={n.testResult ? 34 : 36}>{trim(n.name, n.testResult ? 18 : 27)}</text>
                  {/* last test-run status chip (only on test/last-level nodes that reported one) */}
                  {n.testResult && ds !== 'valid' && ds !== 'linked' && (
                    <text
                      className={`g-run g-run-${n.test || 'none'}`}
                      x={NODE_W - 11}
                      y="35"
                      textAnchor="end"
                    >{runShort(n.testResult)}</text>
                  )}
                  {ds === 'valid' && <text className="g-badge g-badge-add" x={NODE_W - 13} y="19">＋</text>}
                  {ds === 'linked' && <text className="g-badge g-badge-ok" x={NODE_W - 13} y="19">✓</text>}
                  {/* focused-node "related items" button, in the cell — pulses to be noticed (#2) */}
                  {canRelations && isFocus && !linkMode && ds == null && (
                    <g
                      className={`g-relpip${relPulse && relLoadingId !== n.id ? ' pulse' : ''}${relOpen && !relHidden ? ' open' : ''}${relLoadingId === n.id ? ' loading' : ''}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(ev) => { ev.stopPropagation(); if (relLoadingId !== n.id) toggleRelations(n.id); }}
                    >
                      <title>{relOpen ? (relHidden ? 'Show related items' : 'Hide related items') : 'Show related items outside this panel'}</title>
                      <circle cx={NODE_W - 15} cy="15" r="10" />
                      {relLoadingId === n.id
                        ? <Spinner cx={NODE_W - 15} cy={15} />
                        : <text x={NODE_W - 15} y="19.5" textAnchor="middle">{(relOpen && !relHidden) ? '−' : '🔗'}</text>}
                    </g>
                  )}
                </g>
              );
            })}

            {/* external-relation edges (dashed = a cycle / back-reference to a shown item) */}
            {extEdges.map((e, i) => {
              if (hiddenSources.has(e.from) || hiddenNodeIds.has(e.from) || hiddenNodeIds.has(e.to)) return null;
              const a = nodePos(e.from), b = nodePos(e.to);
              if (!a || !b) return null;
              return <path key={`x${i}`} className={`g-extlink${e.existing ? ' g-extlink-cycle' : ''}`} d={extPath(a, b)} />;
            })}

            {/* external (out-of-scope) nodes — amber, type icon+badge, hyperlinked key, recurse expander */}
            {Object.values(extNodes).filter((n) => !hiddenNodeIds.has(n.id)).map((n) => {
              const loading = relLoadingId === n.id;
              const open = expandedIds.has(n.id) && !hiddenSources.has(n.id);
              const icon = typeIcon(n.type, n.code);   // '' unless it's a risk
              const badge = n.type ? trim(n.type, icon ? 11 : 14) : '';  // type LABEL only, never the code
              const badgeX = icon ? 28 : 12;
              const bw = badge ? Math.min(NODE_W - (icon ? 52 : 36), badge.length * 6 + 12) : 0;
              // Show the CUSTOM code (e.g. PR-16775) when we can map the original code (REQ) to it
              const cust = n.code && origToCustom[n.code] ? origToCustom[n.code] : '';
              const displayKey = cust ? `${cust}-${n.id}` : keyPrefix(n.key);
              const keyTxt = trim(displayKey, 16);
              return (
                <g
                  key={`xn-${n.id}`}
                  className="g-node g-ext"
                  transform={`translate(${n.x},${n.y})`}
                  onClick={(ev) => { ev.stopPropagation(); if (!loading) toggleRelations(n.id); }}
                >
                  <title>{n.name ? `${displayKey} — ${n.name}` : displayKey}{n.type ? ` (${n.type})` : ''}</title>
                  <rect className="g-box g-ext-box" x="0" y="0" width={NODE_W} height={EXT_H} rx="9" />
                  {icon && <text className="g-ext-icon" x="13" y="18">{icon}</text>}
                  {badge && <rect className="g-ext-badge" x={badgeX} y="7" width={bw} height="14" rx="7" />}
                  {badge && <text className="g-ext-badge-t" x={badgeX + bw / 2} y="17" textAnchor="middle">{badge}</text>}
                  {/* key — hyperlink to Orcanos when we resolved a url (#10) */}
                  {n.url ? (
                    <a href={n.url} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>
                      <text className="g-key g-ext-key g-ext-link" x="14" y="36">{keyTxt}</text>
                    </a>
                  ) : (
                    <text className="g-key g-ext-key" x="14" y="36">{keyTxt}</text>
                  )}
                  <text className="g-lbl g-ext-lbl" x="14" y="48">{trim(n.name, 24)}</text>
                  {canRelations && (
                    <g className={`g-ext-exp${open ? ' open' : ''}${loading ? ' loading' : ''}`}>
                      <circle cx={NODE_W - 15} cy={EXT_H / 2} r="9" />
                      {loading
                        ? <Spinner cx={NODE_W - 15} cy={EXT_H / 2} />
                        : <text x={NODE_W - 15} y={EXT_H / 2 + 4.5} textAnchor="middle">{open ? '−' : '+'}</text>}
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* confirm dialog */}
      {confirmInfo && (
        <div className="graph-confirm-wrap" onClick={() => setConfirm(null)}>
          <div className="graph-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="gc-title">Create trace?</div>
            <div className="gc-body">
              <span className="gc-key">{confirmInfo.sourceKey}</span>
              <span className="gc-arrow">→</span>
              <span className="gc-key">{confirmInfo.targetKey}</span>
            </div>
            <div className="gc-note">Recorded deeper → shallower in Orcanos.</div>
            <div className="gc-actions">
              <button type="button" className="gc-btn gc-cancel" onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="gc-btn gc-create" onClick={doConfirm} disabled={linking}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* marked link: what it is + Delete */}
      {selInfo && !linkMode && (
        <div className="graph-edgebar" role="status">
          <span className="ge-lbl">Trace</span>
          <span className="gc-key">{selInfo.sourceKey}</span>
          <span className="gc-arrow">→</span>
          <span className="gc-key">{selInfo.targetKey}</span>
          {canDeleteEdge(selEdge) ? (
            <button
              type="button"
              className="gc-btn ge-del"
              onClick={() => setDelConfirm(selEdge)}
              disabled={deleting}
              title="Delete this trace in Orcanos (Delete key)"
            >Delete trace</button>
          ) : (
            <span className="ge-note">You don't have Delete permission on this item type</span>
          )}
          <button type="button" className="ge-close" onClick={() => setSelEdge(null)} aria-label="Unselect">×</button>
        </div>
      )}

      {/* delete confirm */}
      {delInfo && (
        <div className="graph-confirm-wrap" onClick={() => setDelConfirm(null)}>
          <div className="graph-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="gc-title">Delete trace?</div>
            <div className="gc-body">
              <span className="gc-key">{delInfo.sourceKey}</span>
              <span className="gc-arrow">→</span>
              <span className="gc-key">{delInfo.targetKey}</span>
            </div>
            <div className="gc-note">The relation is removed in Orcanos. Both items stay.</div>
            <div className="gc-actions">
              <button type="button" className="gc-btn gc-cancel" onClick={() => setDelConfirm(null)}>Cancel</button>
              <button type="button" className="gc-btn gc-delete" onClick={doDeleteConfirm} disabled={deleting}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* toast */}
      {toast && (
        <div className={`graph-toast ${toast.kind}`} role="status">
          <span className="gt-ic">{toast.kind === 'warn' ? '⚠' : toast.kind === 'info' ? 'ℹ' : '✓'}</span>
          <span className="gt-msg">{toast.msg}</span>
          {toast.retry && (
            <button type="button" className="gt-retry" onClick={() => { const r = toast.retry; setToast(null); r(); }}>Retry</button>
          )}
          <button type="button" className="gt-close" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {focused && !linkMode && (
        <div className="graph-detail">
          <div className="gd-head">
            <span className="gd-key">{keyPrefix(focused.key)}</span>
            <span className="gd-name">{focused.name || '—'}</span>
            <span className="gd-pill gd-lvl">L{levels[focused.levelIdx].level_num} · {levels[focused.levelIdx].name}</span>
            {focused.status === 'fail' && <span className="gd-pill gd-fail">Test failed</span>}
            {focused.status === 'miss' && <span className="gd-pill gd-miss">Chain breaks here</span>}
            {focused.status === 'ok' && focused.test === 'ok' && <span className="gd-pill gd-ok">Test passed</span>}
            {focused.status === 'ok' && focused.test !== 'ok' && <span className="gd-pill gd-ok">In coverage</span>}
            {focused.url && (
              <a className="gd-link" href={focused.url} target="_blank" rel="noopener noreferrer">Open in Orcanos ↗</a>
            )}
            {canAddChild && focused.levelIdx < levels.length - 1 &&
              canAddAtLevel(levels[focused.levelIdx], levels[focused.levelIdx + 1], typeLabels) &&
              canAddChildType(levels[focused.levelIdx + 1]) && (
              <button
                type="button"
                className="gd-relbtn gd-addbtn"
                onClick={() => onAddChild(focused, focused.levelIdx)}
                title={`Add a ${levelAddLabel(levels[focused.levelIdx + 1], typeLabels)} traced to this item`}
              >＋ Add {levelAddLabel(levels[focused.levelIdx + 1], typeLabels)}</button>
            )}
            {canRelations && (() => {
              const open = expandedIds.has(focused.id);
              const hidden = hiddenSources.has(focused.id);
              const loading = relLoadingId === focused.id;
              if (!open) {
                return (
                  <button
                    type="button"
                    className="gd-relbtn"
                    onClick={() => expandRelations(focused.id)}
                    disabled={loading}
                    title="Show items related to this one that are outside the panel filter (risks, defects, CRs)"
                  >{loading ? 'Loading…' : '🔗 Show related'}</button>
                );
              }
              return (
                <span className="gd-relgroup">
                  <button
                    type="button"
                    className={`gd-relbtn${!hidden ? ' on' : ''}`}
                    onClick={() => (hidden ? showHidden(focused.id) : hideRelations(focused.id))}
                    title={hidden ? 'Show the related items again' : 'Hide the related items (keep them loaded)'}
                  >{hidden ? '🔗 Show' : '🙈 Hide'}</button>
                  <button
                    type="button"
                    className="gd-relbtn gd-relclose"
                    onClick={() => closeRelations(focused.id)}
                    title="Remove the related items entirely"
                  >× Close</button>
                </span>
              );
            })()}
          </div>
          <div className="gd-body">
            {focused.testResult && (
              <div>
                <b>Last test run:</b>{' '}
                <span className={`gd-run gd-run-${focused.test || 'none'}`}>{focused.testResult}</span>
              </div>
            )}
            <div>
              <b>Traces up to:</b>{' '}
              <span className="gd-relcount">{parentsOf[focused.id]?.length || 0}</span>{' '}
              {parentsOf[focused.id]?.length
                ? parentsOf[focused.id].map((p) => keyPrefix(nodes.get(p).key)).join(', ')
                : '— (top of chain)'}
            </div>
            <div>
              <b>Traces down to:</b>{' '}
              <span className="gd-relcount">{childrenOf[focused.id]?.length || 0}</span>{' '}
              {childrenOf[focused.id]?.length
                ? childrenOf[focused.id].map((c) => keyPrefix(nodes.get(c).key)).join(', ')
                : <span className="gd-danger">— none (dead-end)</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
